import { InvoiceDataType } from '@/api/InvoiceApi';
import { PriceDataType } from '@/api/PricesApi';
import { EDocumentPaymentWayDataType } from '@/formTypes/EDocumentPaymentWayDataType';
import { EDocumentProductDataType } from '@/formTypes/EDocumentProductDataType';
import { ProductExemptionDataType } from '@/formTypes/ProductExemptionDataType';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DISCOUNT_CODES, ON_INVOICE_DISCOUNT_TYPE_CODE } from '@utils/Constants';
import { calculateProductPrice, generateTemporaryProductCode } from '@utils/EDocument';
import { omitProperties, toNumberFormat } from '@utils/Utils';
import { roundCant } from '@utils/Cant';
import { nanoid } from 'nanoid/non-secure';
import { updateCreditNoteDataState } from './utils/eDocumentUtils';
import { StoredReference } from '@utils/DocumentReferences';
import { StoredOtherCharge } from '@utils/DocumentOtherCharges';
import { ClientShippingOption } from '@utils/ClientShipping';
import { resolveLineTaxForRegime, resolveReceptorTaxRegime } from '@utils/ReceptorTaxRegime';

export interface EDocumentState {
    priceCode?: number;
    hasAdditionalData: boolean;
    priceData?: PriceDataType;
    documentNoteTotal?: number;
    documentDiscount?: number;
    documentDiscountType?: string;
    documentEInvoiceType?: string;
    documentDiscountCode?: keyof typeof DISCOUNT_CODES;
    documentComments?: string;
    isDocumentExempted?: boolean;
    /**
     * Tarifa especial de IVA por quien compra (clave de `RECEPTOR_TAX_REGIMES`). Vacio = cada
     * linea con la tarifa de su producto.
     */
    documentTaxRegime?: string;
    defaultExemption?: ProductExemptionDataType;
    documentProducts?: EDocumentProductDataType[];
    lowStockProducts?: EDocumentProductDataType[];
    priceZeroProducts?: EDocumentProductDataType[];
    noPartidaArancelariaProducts?: EDocumentProductDataType[];
    /** Lineas cuyo CAByS no concuerda con su tipo de producto o con su tarifa de IVA. */
    invalidCabysProducts?: EDocumentProductDataType[];
    documentPaymentWays?: EDocumentPaymentWayDataType[];
    invoiceReference?: InvoiceDataType;
    documentReferences: StoredReference[];
    documentOtherCharges: StoredOtherCharge[];
    /**
     * Opciones de envio del cliente del documento, entre las que se elige al facturar. Es
     * el canal entre el formulario (que conoce al cliente) y la accion de la pagina (que
     * no), igual que ya ocurre con `priceCode` o `documentDiscount`.
     */
    documentShippingOptions: ClientShippingOption[];
    /**
     * Guia de envio pedida para este documento. `null` mientras no se pida ninguna, que es
     * lo que decide si la factura nace con bultos y si su impresion la incluye.
     */
    documentShippingGuide: DocumentShippingGuide | null;
}

/**
 * Lo que la guia necesita del documento en edicion. El nombre del transporte se resuelve
 * al agregar el envio (no al imprimir) para que quede congelado en la factura.
 */
export type DocumentShippingGuide = {
    /** Cantidad de bultos: sale impresa y define cuantas copias de la guia se imprimen. */
    bultos: number;
    carrier: string;
}
export interface DocumentSummaryProps {
    subtotal: number
    descuento: number,
    impuestos: number,
    total: number,
    /** Campo "TotalIVADevuelto" del resumen del comprobante (servicios de salud pagados con tarjeta). */
    ivaDevuelto?: number,
    /** Campo "TotalOtrosCargos" del resumen del comprobante (nodo OtrosCargos v4.4). */
    otrosCargos?: number,
    /** Rotulo de la fila de otros cargos. Vacio = etiqueta generica "Otros Cargos". */
    otrosCargosLabel?: string
}
interface ToogleExemptionProp {
    exempted: boolean;
    id: string;
}
interface AddProductExemptionProps {
    exemption?: ProductExemptionDataType;
    productId?: string;
}

export interface ProductPropertyDataProp {
    property: keyof EDocumentProductDataType,
    value: any,
    id: string;
}

interface PaymentWayPropertyDataProp {
    property: keyof EDocumentPaymentWayDataType,
    value: any,
    id: string;
}

const initialState: EDocumentState = {
    hasAdditionalData: false,
    isDocumentExempted: false,
    documentTaxRegime: "",
    documentProducts: [],
    lowStockProducts: [],
    noPartidaArancelariaProducts: [],
    invalidCabysProducts: [],
    documentPaymentWays: [],
    documentDiscount: 0,
    documentNoteTotal: 0,
    invoiceReference: {},
    documentDiscountType: ON_INVOICE_DISCOUNT_TYPE_CODE,
    documentDiscountCode: undefined,
    documentReferences: [],
    documentOtherCharges: [],
    documentShippingOptions: [],
    documentShippingGuide: null
};

const recalculateProductData = (product: EDocumentProductDataType, state: EDocumentState): void => {
    if (!!`${product.price}` && !!`${product.cant}` && product.price != null && product.cant != null) {
        product.subtotal = Number(toNumberFormat(product.cant * product.price))
    }
    const subTotal = Number(product.subtotal || 0)

    let descuento = Number()
    if (product.porcentaje_descuento != null) {
        const porcentajeDescuento = product.porcentaje_descuento / 100
        descuento = Number(toNumberFormat(subTotal * porcentajeDescuento));
        product.descuento = descuento
    }

    if (product.exento) {
        product.impuestos = 0
    } else {
        let impuestos = Number(0)
        let porcentajeImpuestos = Number(product.porcentaje_impuesto || 0)
        porcentajeImpuestos = porcentajeImpuestos / 100

        if (state.documentDiscountType === ON_INVOICE_DISCOUNT_TYPE_CODE) {
            impuestos = (subTotal - descuento) * porcentajeImpuestos
        } else {
            impuestos = subTotal * porcentajeImpuestos
        }
        product.impuestos = Number(toNumberFormat(impuestos))
    }
}

/** "general", vacio o una clave desconocida quedan como "" (sin tarifa especial). */
const normalizeTaxRegime = (key?: string | null): string =>
    resolveReceptorTaxRegime(key) ? `${key}` : ""

const applyDocumentTaxRegime = (product: EDocumentProductDataType, state: EDocumentState): void => {
    const tax = resolveLineTaxForRegime(product, state.documentTaxRegime)
    if (tax) {
        product.porcentaje_impuesto = tax.porcentaje_impuesto
        product.codigo_tarifa = tax.codigo_tarifa
    }
}

const recalculateAllProductsData = (state: EDocumentState) => {
    if (state.documentProducts) {
        const newProducts = [
            ...state.documentProducts
        ];
        newProducts.forEach(p => {
            if (p.product && !p.product.temporal) {
                const newPrice = Number(calculateProductPrice(p.product, state.priceCode || state.priceData))
                p.price = newPrice;
            }
            recalculateProductData(p, state);
        })
        state.documentProducts = newProducts
    }
}

function normalizeOrderIndex(products: EDocumentProductDataType[]): EDocumentProductDataType[] {
    return products
        .sort((a, b) => Number(a.orderIndex || 0) - Number(b.orderIndex || 0))
        .map((p, i) => ({ ...p, orderIndex: i }));
}

const eDocumentSlice = createSlice({
    name: 'eDocument',
    initialState,
    reducers: {
        setPrice(state, action: PayloadAction<PriceDataType>) {
            state.priceData = action.payload
            if (state.priceCode != action.payload) {
                recalculateAllProductsData(state)
            }
        },
        setPriceCode(state, action: PayloadAction<number>) {
            state.priceCode = action.payload
            if (state.priceCode != action.payload) {
                recalculateAllProductsData(state)
            }
        },
        setDocumentNoteTotal(state, action: PayloadAction<number>) {
            state.documentNoteTotal = action.payload
            updateCreditNoteDataState(state)
            // recalculateAllProductsData(state)
        },
        updateCreditNoteData(state) {
            updateCreditNoteDataState(state)
        },
        hasAdditionalData(state, action: PayloadAction<boolean>) {
            state.hasAdditionalData = action.payload
        },
        setInvoiceReference(state, action: PayloadAction<InvoiceDataType>) {
            state.invoiceReference = action.payload
        },
        setDocumentDiscount(state, action: PayloadAction<number>) {
            state.documentDiscount = action.payload
            if (state.documentProducts) {
                const newProducts = [
                    ...state.documentProducts
                ];
                newProducts.forEach(p => {
                    p.porcentaje_descuento = action.payload
                    recalculateProductData(p, state);
                })
                state.documentProducts = newProducts
            }
        },
        setDocumentEInvoiceType(state, action: PayloadAction<string>) {
            state.documentEInvoiceType = action.payload
        },
        setDocumentDiscountType(state, action: PayloadAction<string>) {
            state.documentDiscountType = action.payload
            state.documentProducts?.forEach((p) => {
                recalculateProductData(p, state);
            });
        },
        setDocumentDiscountCode(state, action: PayloadAction<keyof typeof DISCOUNT_CODES>) {
            state.documentDiscountCode = action.payload
        },
        setDocumentComments(state, action: PayloadAction<string>) {
            state.documentComments = action.payload
        },
        setDefaultExemption(state, action: PayloadAction<ProductExemptionDataType>) {
            state.defaultExemption = action.payload
        },
        setDocumentExempted(state, action: PayloadAction<boolean>) {
            state.isDocumentExempted = action.payload
        },
        /**
         * Cambia la tarifa especial del receptor y la aplica a las lineas ya cargadas. Si no
         * cambia no toca nada, para respetar las tarifas que el usuario edito a mano.
         */
        setDocumentTaxRegime(state, action: PayloadAction<string | null | undefined>) {
            const regime = normalizeTaxRegime(action.payload)
            if ((state.documentTaxRegime || "") === regime) {
                return
            }
            state.documentTaxRegime = regime
            state.documentProducts?.forEach((p) => {
                applyDocumentTaxRegime(p, state)
                recalculateProductData(p, state)
            })
        },
        /**
         * Restaura la tarifa especial de un documento guardado sin recalcular sus lineas: se
         * despacha antes de `loadProducts` para que las lineas conserven la tarifa con que se
         * guardaron.
         */
        restoreDocumentTaxRegime(state, action: PayloadAction<string | null | undefined>) {
            state.documentTaxRegime = normalizeTaxRegime(action.payload)
        },
        clearDefaultExemption(state) {
            delete state.defaultExemption
        },
        setLowStockProducts(state, action: PayloadAction<EDocumentProductDataType[]>) {
            state.lowStockProducts = action.payload
        },
        setPriceZeroProducts(state, action: PayloadAction<EDocumentProductDataType[]>) {
            state.priceZeroProducts = action.payload
        },
        setNoPartidaArancelariaProducts(state, action: PayloadAction<EDocumentProductDataType[]>) {
            state.noPartidaArancelariaProducts = action.payload
        },
        setInvalidCabysProducts(state, action: PayloadAction<EDocumentProductDataType[]>) {
            state.invalidCabysProducts = action.payload
        },
        addProduct(state, action: PayloadAction<EDocumentProductDataType>) {
            const newProduct = {
                ...action.payload,
                porcentaje_descuento: state.documentDiscount,
                id: `p-${nanoid(10)}`
            }
            const { forceRecalculatePrice = false } = action.payload

            let calculatedPrice;
            if (forceRecalculatePrice && newProduct.product) {
                calculatedPrice = Number(calculateProductPrice(newProduct.product, state.priceCode || state.priceData))
            }

            const existingProduct = state.documentProducts?.find(p => p.productId && p.productId == newProduct.productId);
            if (existingProduct?.id && existingProduct.cant && newProduct.cant) {
                existingProduct.cant = roundCant(existingProduct.cant + newProduct.cant)
                existingProduct.price = calculatedPrice || existingProduct.price
                recalculateProductData(existingProduct, state);
            } else {
                applyDocumentTaxRegime(newProduct, state);
                const temporalProductCode = generateTemporaryProductCode(state.documentProducts, newProduct);
                newProduct.codigo = newProduct.codigo || temporalProductCode
                if (newProduct.product?.temporal) {
                    newProduct.price = newProduct.product.precio1
                } else {
                    newProduct.price = calculatedPrice || newProduct.price
                }
                recalculateProductData(newProduct, state);
                const nextIndex = state.documentProducts?.length || 0;
                state.documentProducts?.push({ ...newProduct, orderIndex: nextIndex })
            }
        },
        splitProduct(state, action: PayloadAction<string>) {
            const originalProduct = state.documentProducts?.find(p => p.id == action.payload)
            let newProduct;
            if (originalProduct) {
                const totalCantidad = originalProduct.cant || 0;
                const mitad = roundCant(totalCantidad / 2);
                const cantidad2 = roundCant(totalCantidad - mitad);
                // Si la cantidad no alcanza para dos mitades positivas (ej. 0.001), se clona la línea
                if (mitad <= 0 || cantidad2 <= 0) {
                    const clonedProduct = {
                        ...originalProduct,
                        id: `cb-${nanoid(10)}`
                    };
                    newProduct = clonedProduct;
                } else {

                    originalProduct.cant = mitad;
                    recalculateProductData(originalProduct, state);

                    // Add the second line
                    newProduct = {
                        ...originalProduct,
                        id: `cb-${nanoid(10)}`,
                        cant: cantidad2
                    };
                    recalculateProductData(newProduct, state);
                }

            }
            if (newProduct) {
                const newProductList = [...(state.documentProducts || []),
                omitProperties(newProduct, ["preSavedId"])
                ];
                state.documentProducts = normalizeOrderIndex(newProductList);
            }
        },
        removeProduct(state, action: PayloadAction<string>) {
            const otherProducts = state.documentProducts?.filter(p => p.id !== action.payload)
            const toRemoveProduct = state.documentProducts?.find(p => p.id == action.payload)
            if (toRemoveProduct && toRemoveProduct.preSavedId) {
                toRemoveProduct.toDelete = true;
                const newProducts = [...otherProducts, toRemoveProduct]
                state.documentProducts = normalizeOrderIndex(newProducts as EDocumentProductDataType[])
            } else {
                state.documentProducts = normalizeOrderIndex(otherProducts as EDocumentProductDataType[])
            }

        },
        resetProductsExemption(state) {
            if (state.documentProducts) {
                const newProducts = [
                    ...state.documentProducts
                ];
                newProducts.forEach(p => {
                    p.exento = p.product?.exento
                    recalculateProductData(p, state);
                })
                state.documentProducts = newProducts
            }
        },
        makeProductsExempted(state) {
            if (state.documentProducts) {
                const newProducts = [
                    ...state.documentProducts
                ];
                newProducts.forEach(p => {
                    p.exento = true
                    recalculateProductData(p, state);
                })
                state.documentProducts = newProducts
            }
        },
        setProductExempted(state, action: PayloadAction<ToogleExemptionProp>) {
            const product = state.documentProducts?.find(p => p.id == action.payload.id)
            if (product) {
                product.exento = action.payload.exempted
                recalculateProductData(product, state);
            }
        },
        setProductExemptionData(state, action: PayloadAction<AddProductExemptionProps>) {
            const product = state.documentProducts?.find(p => p.id == action.payload.productId)
            if (product) {
                product.exemption = action.payload.exemption
                recalculateProductData(product, state);
            }
        },
        setAllProductsExemptionData(state, action: PayloadAction<AddProductExemptionProps>) {
            state.documentProducts?.forEach((p) => {
                p.exemption = action.payload.exemption
                recalculateProductData(p, state);
            });
        },
        clearAllProductsExemptionData(state) {
            state.documentProducts?.forEach((p) => {
                delete p.exemption
                recalculateProductData(p, state);
            })
        },
        clearProductExemptionData(state, action: PayloadAction<string>) {
            const product = state.documentProducts?.find(p => p.id == action.payload)
            if (product) {
                delete product.exemption
                recalculateProductData(product, state);
            }
        },
        setProductPropertyData(state, action: PayloadAction<ProductPropertyDataProp>) {
            const product = state.documentProducts?.find(p => p.id == action.payload.id)
            if (product) {
                product[action.payload.property] = action.payload.value
                recalculateProductData(product, state);
            }
        },
        setProductPropertiesData(state, action: PayloadAction<Partial<EDocumentProductDataType>>) {
            const product = state.documentProducts?.find(p => p.id == action.payload.id)
            if (product) {
                Object.entries(action.payload).forEach(([property, value]) => {
                    if (property && property != "id") {
                        (product as any)[property] = `${value}`
                    }
                })
                recalculateProductData(product, state);
            }
        },
        removeZeroProductLines(state) {
            const noneZeroProductLines = state.documentProducts?.filter(p => p.subtotal != null && p.subtotal > 0)
            state.documentProducts = normalizeOrderIndex(noneZeroProductLines as EDocumentProductDataType[])
        },
        reorderProductsByIds(state, action: PayloadAction<string[]>) {
            const idOrder = action.payload;
            const byId = new Map(state.documentProducts?.map(p => [p.id, p]));
            const reordered: EDocumentProductDataType[] = [];
            idOrder.forEach((id, i) => {
                const item = byId.get(id);
                if (item) reordered.push({ ...item, orderIndex: i });
            });
            // por si había productos fuera de la lista (no debería)
            const remaining = state.documentProducts?.filter(p => !byId.has(p.id));
            reordered.push(...(remaining as EDocumentProductDataType[]));
            state.documentProducts = reordered;
        },
        clearProducts(state) {
            state.documentProducts = []
        },
        loadProducts(state, action: PayloadAction<EDocumentProductDataType[]>) {
            const newProducts = action.payload.map(p => {
                let product = {
                    ...p,
                    id: `pw-${nanoid(10)}`
                }
                product = omitProperties(product, ["invoiceTemplateId", "invoiceTemplate", "createdAt", "updatedAt"])
                recalculateProductData(product, state);
                return product
            });
            state.documentProducts = normalizeOrderIndex(newProducts)
        },
        addPaymentWay(state, action: PayloadAction<EDocumentPaymentWayDataType>) {
            const newProduct = {
                ...action.payload,
                id: `pw-${nanoid(10)}`
            }
            state.documentPaymentWays?.push(newProduct)
        },
        removePaymentWay(state, action: PayloadAction<string>) {
            const otherProducts = state.documentPaymentWays?.filter(p => p.id !== action.payload)
            state.documentPaymentWays = otherProducts
        },
        setPaymentWayPropertyData(state, action: PayloadAction<PaymentWayPropertyDataProp>) {
            const product = state.documentPaymentWays?.find(p => p.id == action.payload.id)
            if (product) {
                product[action.payload.property] = action.payload.value
            }
        },
        clearPaymentWays(state) {
            state.documentPaymentWays = []
        },
        setDocumentReferences(state, action: PayloadAction<StoredReference[]>) {
            state.documentReferences = action.payload
        },
        clearDocumentReferences(state) {
            state.documentReferences = []
        },
        setDocumentOtherCharges(state, action: PayloadAction<StoredOtherCharge[]>) {
            state.documentOtherCharges = action.payload
        },
        clearDocumentOtherCharges(state) {
            state.documentOtherCharges = []
        },
        setDocumentShippingOptions(state, action: PayloadAction<ClientShippingOption[]>) {
            state.documentShippingOptions = action.payload
        },
        clearDocumentShippingOptions(state) {
            state.documentShippingOptions = []
        },
        setDocumentShippingGuide(state, action: PayloadAction<DocumentShippingGuide>) {
            state.documentShippingGuide = action.payload
        },
        clearDocumentShippingGuide(state) {
            state.documentShippingGuide = null
        },
        clearDocument(state) {
            state.priceCode = initialState.priceCode;
            state.priceData = initialState.priceData;
            state.documentDiscount = initialState.documentDiscount;
            state.documentDiscountType = initialState.documentDiscountType;
            state.documentDiscountCode = initialState.documentDiscountCode;
            state.documentEInvoiceType = initialState.documentEInvoiceType;
            state.documentComments = initialState.documentComments;
            state.lowStockProducts = initialState.lowStockProducts;
            state.priceZeroProducts = initialState.priceZeroProducts;
            state.noPartidaArancelariaProducts = initialState.noPartidaArancelariaProducts;
            state.invalidCabysProducts = initialState.invalidCabysProducts;
            state.isDocumentExempted = initialState.isDocumentExempted;
            state.documentTaxRegime = initialState.documentTaxRegime;
            state.defaultExemption = initialState.defaultExemption;
            state.documentProducts = initialState.documentProducts;
            state.documentPaymentWays = initialState.documentPaymentWays;
            state.documentNoteTotal = initialState.documentNoteTotal;
            state.invoiceReference = initialState.invoiceReference;
            state.documentReferences = initialState.documentReferences;
            state.documentOtherCharges = initialState.documentOtherCharges;
            state.documentShippingOptions = initialState.documentShippingOptions;
            state.documentShippingGuide = initialState.documentShippingGuide;
        }
    },
    extraReducers() {
    },
});

export const { setPrice, setPriceCode, addProduct, splitProduct, removeProduct, setDocumentEInvoiceType,
    resetProductsExemption, makeProductsExempted, setProductExempted, setProductPropertyData,
    clearProducts, removeZeroProductLines, setDocumentDiscount, setDocumentDiscountType, setDocumentDiscountCode,
    setDocumentComments, addPaymentWay, removePaymentWay, setPaymentWayPropertyData, clearPaymentWays,
    setProductPropertiesData, setProductExemptionData, setLowStockProducts, setPriceZeroProducts,
    setNoPartidaArancelariaProducts, setInvalidCabysProducts, setAllProductsExemptionData, clearAllProductsExemptionData, clearProductExemptionData, hasAdditionalData,
    setDefaultExemption, clearDefaultExemption, setDocumentExempted, setDocumentTaxRegime, restoreDocumentTaxRegime, clearDocument, reorderProductsByIds,
    loadProducts, setDocumentNoteTotal, setInvoiceReference, updateCreditNoteData,
    setDocumentReferences, clearDocumentReferences,
    setDocumentOtherCharges, clearDocumentOtherCharges,
    setDocumentShippingOptions, clearDocumentShippingOptions,
    setDocumentShippingGuide, clearDocumentShippingGuide } = eDocumentSlice.actions;
export default eDocumentSlice.reducer;